import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search as SearchIcon, Save } from 'lucide-react';
import {
  getSearchTracks, getSearchVehicles, getSearchReferenceLap,
  getTrack, saveTrack, getSessionLaps, getLapTrace,
} from '../../services/api';
import { defaultSectors } from '../../utils/sectors';
import SpeedTrace from '../Analysis/SpeedTrace';
import TrackMap from '../TrackMap/TrackMap';
import SectorResultsPanel from './SectorResultsPanel';
import PointQueryPanel from './PointQueryPanel';
import styles from './SearchPage.module.css';

const M_TO_FT = 3.28084;

const SCOPES = [
  { key: 'mine',   label: 'My data' },
  { key: 'shared', label: 'Shared with me' },
  { key: 'all',    label: 'All' },
];

/**
 * Search page — cross-session queries at a track:
 *   • sector search: define sectors (seeded from the track's saved sectors,
 *     edited with the SpeedTrace sector editor) and rank the best sector times
 *     across every in-scope lap;
 *   • point search: click the trace or map to pick a spot, then rank laps by
 *     any logged channel's value at that spot.
 *
 * The displayed trace/map is a single "reference lap" — the fastest in-scope
 * lap at the track. Sector edits stay local until "Save sectors to track".
 */
export default function SearchPage({
  onBack, onLoadSession, initialParams, onParamsChange, currentUser, unitPrefs,
  onGoToLeaderboard, onGoToProgress, onGoToChannelStats, onGoToSimLab,
  onOpenUserSettings, onLogout, driverProfile,
}) {
  const [scope, setScope] = useState(initialParams?.scope || 'mine');
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState(initialParams?.trackId || null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(initialParams?.vehicleId ?? null);   // null = all cars

  const [trackDef, setTrackDef] = useState(null);   // {track_name, corners, ...}
  const [sectors, setSectors] = useState([]);
  const [sectorsDirty, setSectorsDirty] = useState(false);
  const [savingSectors, setSavingSectors] = useState(false);
  // Unsaved sector edits restored from a Back-navigation/deep-link: consumed
  // once by the track-definition effect below, then left alone so a later
  // track switch loads that track's real saved sectors as usual.
  const restoreSectorsRef = useRef(
    initialParams?.sectorsDirty && initialParams?.sectors?.length ? initialParams.sectors : null);

  const [refLap, setRefLap] = useState(null);       // /search/reference_lap row
  const [refLaps, setRefLaps] = useState([]);       // reference session's lap list
  const [refIndex, setRefIndex] = useState(null);   // lap index within that session
  const [refTrace, setRefTrace] = useState(null);

  const [pointFrac, setPointFrac] = useState(initialParams?.pointFrac ?? null); // 0..1 of lap distance
  // First run of the reference-lap effect (below) shouldn't clobber a
  // restored pointFrac before the reference lap has even loaded.
  const firstRefEffectRef = useRef(true);
  const [refLoading, setRefLoading] = useState(false);
  const [error, setError] = useState(null);
  // Latest reported state from the two results panels — round-tripped through
  // initialParams/onParamsChange so a Back-navigation/deep-link can tell each
  // panel to auto re-run the search it had already completed.
  const [sectorSearchState, setSectorSearchState] = useState(initialParams?.sectorSearch || null);
  const [pointQueryState, setPointQueryState] = useState(initialParams?.pointQuery || null);
  // Same persisted preference the analysis view's map uses.
  const [showTurnMarkers, setShowTurnMarkers] = useState(
    () => localStorage.getItem('trackmap.showTurnMarkers') !== 'false');

  // Trace+map panel height: natively resizable (CSS resize), persisted.
  const refGridEl = useRef(null);
  const [refGridH] = useState(() => {
    const v = parseInt(localStorage.getItem('search.refGridH') || '', 10);
    return Number.isFinite(v) && v >= 280 ? v : 480;
  });
  useEffect(() => {
    const el = refGridEl.current;
    if (!el) return;
    let t = null;
    const ro = new ResizeObserver(entries => {
      const h = Math.round(entries[0].contentRect.height);
      clearTimeout(t);
      t = setTimeout(() => localStorage.setItem('search.refGridH', String(h)), 300);
    });
    ro.observe(el);
    return () => { clearTimeout(t); ro.disconnect(); };
  });

  // ── Track list under the scope ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getSearchTracks(scope).then(rows => {
      if (cancelled) return;
      setTracks(rows || []);
      setTrackId(prev =>
        (rows?.some(t => t.track_id === prev) ? prev : (rows?.[0]?.track_id || null)));
    }).catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [scope]);

  // ── Vehicles at the selected track (car filter; null = all) ──────────────
  useEffect(() => {
    if (!trackId) { setVehicles([]); setVehicleId(null); return; }
    let cancelled = false;
    getSearchVehicles(trackId, scope).then(vs => {
      if (cancelled) return;
      setVehicles(vs || []);
      // Keep the current pick if it still exists in the new scope/track.
      setVehicleId(prev => (vs?.some(v => v.vehicle_id === prev) ? prev : null));
    }).catch(() => !cancelled && setVehicles([]));
    return () => { cancelled = true; };
  }, [trackId, scope]);

  // ── Track definition → sectors (saved ones, else 10 equal defaults) ──────
  useEffect(() => {
    if (!trackId) { setTrackDef(null); setSectors([]); return; }
    let cancelled = false;
    getTrack(trackId).then(t => {
      if (cancelled) return;
      setTrackDef(t);
      if (restoreSectorsRef.current) {
        setSectors(restoreSectorsRef.current);
        setSectorsDirty(true);
        restoreSectorsRef.current = null;
      } else {
        setSectors(t?.sectors?.length ? t.sectors : defaultSectors(10));
        setSectorsDirty(false);
      }
    }).catch(() => {
      if (cancelled) return;
      setTrackDef(null);
      if (restoreSectorsRef.current) {
        setSectors(restoreSectorsRef.current);
        setSectorsDirty(true);
        restoreSectorsRef.current = null;
      } else {
        setSectors(defaultSectors(10));
        setSectorsDirty(false);
      }
    });
    return () => { cancelled = true; };
  }, [trackId]);

  // ── Reference lap (fastest in scope) → its session's laps → its trace ────
  useEffect(() => {
    if (!trackId) { setRefLap(null); setRefTrace(null); setRefIndex(null); return; }
    let cancelled = false;
    setRefLap(null); setRefTrace(null); setRefIndex(null);
    if (firstRefEffectRef.current) firstRefEffectRef.current = false;
    else setPointFrac(null);
    setRefLoading(true);
    (async () => {
      try {
        const ref = await getSearchReferenceLap(trackId, scope, vehicleId);
        if (cancelled) return;
        setRefLap(ref);
        const laps = await getSessionLaps(ref.session_id);
        if (cancelled) return;
        setRefLaps(laps || []);
        const lap = (laps || []).find(l => l.lap_number === ref.lap_number);
        if (!lap) return;
        const trace = await getLapTrace(ref.session_id, lap.index);
        if (cancelled) return;
        setRefIndex(lap.index);
        setRefTrace(trace);
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setRefLap(null);
          setError(/404/.test(String(e)) ? null : String(e.message || e));
        }
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [trackId, scope, vehicleId]);

  const lapTotalFt = useMemo(() => {
    const pts = refTrace?.points;
    if (!pts?.length) return null;
    let max = 0;
    for (const p of pts) if (p.dist_m != null && p.dist_m > max) max = p.dist_m;
    return max > 0 ? max * M_TO_FT : null;
  }, [refTrace]);

  // ── Point selection: clicks on the trace or the map pick a lap fraction ──
  useEffect(() => {
    const onClick = (e) => {
      const distFt = e?.detail?.distFt;
      if (distFt == null || !lapTotalFt) return;
      setPointFrac(Math.max(0, Math.min(1, distFt / lapTotalFt)));
    };
    window.addEventListener('trackapp:chart-click', onClick);
    window.addEventListener('trackapp:map-click', onClick);
    return () => {
      window.removeEventListener('trackapp:chart-click', onClick);
      window.removeEventListener('trackapp:map-click', onClick);
    };
  }, [lapTotalFt]);

  // ── Mirror the full filter/selection state up to App.jsx (→ URL) so a
  // Back-navigation or deep-link can restore this page exactly as left. ────
  useEffect(() => {
    onParamsChange?.({
      trackId, scope, vehicleId, sectors, sectorsDirty, pointFrac,
      sectorSearch: sectorSearchState, pointQuery: pointQueryState,
    });
  }, [trackId, scope, vehicleId, sectors, sectorsDirty, pointFrac,
      sectorSearchState, pointQueryState, onParamsChange]);

  const handleSectorsChange = useCallback((next) => {
    setSectors(next);
    setSectorsDirty(true);
  }, []);

  const handleSaveSectors = useCallback(async () => {
    if (!trackId) return;
    setSavingSectors(true);
    try {
      // POST /tracks applies sector edits for any user and keeps the stored
      // corners for non-admins, so passing what we loaded is safe.
      await saveTrack({
        track_id: trackId,
        track_name: trackDef?.track_name
          || tracks.find(t => t.track_id === trackId)?.track_name || trackId,
        corners: trackDef?.corners || [],
        sectors,
      });
      setSectorsDirty(false);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSavingSectors(false);
    }
  }, [trackId, trackDef, tracks, sectors]);

  const lapTraces = useMemo(
    () => (refIndex != null && refTrace ? { [refIndex]: refTrace } : {}),
    [refIndex, refTrace]);
  const selectedLaps = useMemo(
    () => (refIndex != null && refTrace ? [refIndex] : []),
    [refIndex, refTrace]);

  const track = tracks.find(t => t.track_id === trackId);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}><SearchIcon size={18} /> Search</h1>
        <div className={styles.spacer} />
      </div>

      {/* ── Scope + track ─────────────────────────────────────────────── */}
      <div className={styles.controls}>
        <div className={styles.ctl}>
          <span>Search in</span>
          <div className={styles.segmented}>
            {SCOPES.map(s => (
              <button key={s.key}
                className={scope === s.key ? styles.segOn : styles.seg}
                onClick={() => setScope(s.key)}>{s.label}</button>
            ))}
          </div>
        </div>
        <label className={styles.ctl}>
          <span>Track</span>
          <select value={trackId || ''} onChange={e => setTrackId(e.target.value || null)}>
            {tracks.map(t => (
              <option key={t.track_id} value={t.track_id}>{t.track_name}</option>
            ))}
          </select>
        </label>
        <label className={styles.ctl}>
          <span>Car</span>
          <select value={vehicleId || ''} onChange={e => setVehicleId(e.target.value || null)}>
            <option value="">All cars</option>
            {vehicles.map(v => (
              <option key={v.vehicle_id} value={v.vehicle_id}>
                {v.name} ({v.lap_count} laps)
              </option>
            ))}
          </select>
        </label>
        <div className={styles.scopeNote}>
          {vehicleId
            ? `${vehicles.find(v => v.vehicle_id === vehicleId)?.lap_count ?? '—'} laps for this car`
            : track ? `${track.lap_count} laps across ${track.session_count} session${track.session_count === 1 ? '' : 's'}` : ''}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!tracks.length && !error && (
        <div className={styles.empty}>
          No searchable sessions in this scope yet. Sessions become searchable
          shortly after upload.
        </div>
      )}

      {trackId && (
        <>
          {/* ── Reference lap: trace (with sector editor) + map ─────────── */}
          <section className={styles.card}>
            <div className={styles.cardHead}>
              <span>Reference lap
                {refLap && (
                  <span className={styles.refMeta}>
                    {' '}— {refLap.lap_time_str} by {refLap.user_display_name || 'unknown'}
                    {refLap.vehicle_name ? ` · ${refLap.vehicle_name}` : ''}
                    {refLap.event_date ? ` · ${refLap.event_date}` : ''}
                    {' '}(fastest in scope)
                  </span>
                )}
              </span>
              <span className={styles.headActions}>
                <span className={styles.hint}>
                  Use the trace’s Sectors button to edit boundaries · click trace or map to pick a point
                </span>
                {sectorsDirty && <span className={styles.dirtyBadge}>unsaved sectors</span>}
                <button
                  className={styles.saveBtn}
                  onClick={handleSaveSectors}
                  disabled={!sectorsDirty || savingSectors}
                  title="Persist these sector boundaries to the track (shared by every session at this track)"
                >
                  <Save size={12} /> {savingSectors ? 'Saving…' : 'Save sectors to track'}
                </button>
              </span>
            </div>
            {refTrace ? (
              <div className={styles.refGrid} ref={refGridEl} style={{ height: refGridH }}>
                <div className={styles.traceBox}>
                  <SpeedTrace
                    lapTraces={lapTraces}
                    selectedLaps={selectedLaps}
                    sectors={sectors}
                    onSectorsChange={handleSectorsChange}
                    showSectorBands
                    sessionId={refLap?.session_id}
                    unitPrefs={unitPrefs}
                    anchoredTooltip={!!unitPrefs?.anchoredTooltip}
                    trackName={track?.track_name}
                  />
                </div>
                <div className={styles.mapBox}>
                  <TrackMap
                    laps={refLaps}
                    lapTraces={lapTraces}
                    selectedLaps={selectedLaps}
                    corners={trackDef?.corners || []}
                    canEditCorners={false}
                    sectors={sectors}
                    initialColorMode="sector"
                    showTurnMarkers={showTurnMarkers}
                    onToggleShowTurnMarkers={() => setShowTurnMarkers(v => {
                      localStorage.setItem('trackmap.showTurnMarkers', String(!v));
                      return !v;
                    })}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.empty}>
                {refLoading ? 'Loading reference lap…' : 'No searchable laps at this track in this scope.'}
              </div>
            )}
          </section>

          <SectorResultsPanel
            trackId={trackId}
            scope={scope}
            vehicleId={vehicleId}
            sectors={sectors}
            onLoadSession={onLoadSession}
            initialState={sectorSearchState}
            onStateChange={setSectorSearchState}
          />

          <PointQueryPanel
            trackId={trackId}
            scope={scope}
            vehicleId={vehicleId}
            pointFrac={pointFrac}
            lapTotalFt={lapTotalFt}
            unitPrefs={unitPrefs}
            onLoadSession={onLoadSession}
            initialState={pointQueryState}
            onStateChange={setPointQueryState}
          />
        </>
      )}
    </div>
  );
}
